import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type ClaimRequest } from '@/api/endpoints/admin';
import { Card, CardContent, PageSpinner, Button, Badge } from '@/components/ui';
import { Check, X, Building2, Mail, Globe, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ClaimRequestCard({
  request,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: {
  request: ClaimRequest;
  onApprove: () => void;
  onReject: () => void;
  isApproving: boolean;
  isRejecting: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-3">
            {/* Bar info */}
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[var(--color-muted)]" />
              <div>
                <p className="font-medium">{request.barName}</p>
                <p className="text-sm text-[var(--color-muted)]">{request.barAddress}</p>
              </div>
            </div>

            {/* User info */}
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[var(--color-muted)]" />
              <div>
                <p className="text-sm">
                  {request.userName || 'Unknown'} ({request.userEmail})
                </p>
              </div>
            </div>

            {/* Website */}
            {request.barWebsite && (
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-[var(--color-muted)]" />
                <a
                  href={request.barWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-accent-secondary hover:underline"
                >
                  {request.barWebsite}
                </a>
              </div>
            )}

            {/* Request date */}
            <div className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
              <Clock className="h-4 w-4" />
              Requested {formatDate(request.createdAt)}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={onApprove}
              disabled={isApproving || isRejecting}
              className="flex items-center gap-1"
            >
              <Check className="h-4 w-4" />
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onReject}
              disabled={isApproving || isRejecting}
              className="flex items-center gap-1 text-red-400 hover:text-red-300"
            >
              <X className="h-4 w-4" />
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ClaimRequestsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['claim-requests'],
    queryFn: adminApi.getClaimRequests,
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => adminApi.approveClaimRequest(id),
    onSuccess: () => {
      toast.success('Claim request approved');
      queryClient.invalidateQueries({ queryKey: ['claim-requests'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to approve request');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => adminApi.rejectClaimRequest(id),
    onSuccess: () => {
      toast.success('Claim request rejected');
      queryClient.invalidateQueries({ queryKey: ['claim-requests'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reject request');
    },
  });

  if (isLoading) return <PageSpinner />;

  const requests = data?.requests || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Claim Requests</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Review and approve bar ownership claims
        </p>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-[var(--color-muted)]">
            No pending claim requests
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Badge variant="secondary">{requests.length} pending</Badge>
          {requests.map((request) => (
            <ClaimRequestCard
              key={request.id}
              request={request}
              onApprove={() => approveMutation.mutate(request.id)}
              onReject={() => rejectMutation.mutate(request.id)}
              isApproving={approveMutation.isPending && approveMutation.variables === request.id}
              isRejecting={rejectMutation.isPending && rejectMutation.variables === request.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
